import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Chip,
    Container,
    Divider,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import {Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams} from 'react-router';
import dayjs from 'dayjs';
import {canAccessCollectionTab, sanitizeReturnTo} from '../collection/collectionTaskPayload';
import {
    getGapByNumber,
    getGapRelatedEeis,
    updateGapPriority,
    updateGapTargets,
} from '../../firebase/firestore/icr';
import {getUserDoc} from '../../firebase/firestore/users';
import {useSession} from '../../firebase/UserProvider';
import AuthorChip from '../products/components/common/AuthorChip';
import {toAvatarProfile, userDisplayName} from '../collection/components/PersonCell';
import {coverageScoreColor, coverageTrackColor} from './coverage/coverageScoreColor';
import {
    resolveGapFintelPath,
    TYPE_COLORS,
} from './similarity/similarityLabels';
import {useGapSimilarity} from './similarity/useGapSimilarity';
import EeiSimilaritySection from './similarity/EeiSimilaritySection';
import RequirementCollectionPanel from './components/RequirementCollectionPanel';
import RequirementTargetsSection from './components/RequirementTargetsSection';
import GapPriorityCell from './components/GapPriorityCell';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from './tags/EntityTagsSection';

const PRIORITY_VALUES = ['High', 'Medium', 'Low'];
/** Non-empty Select value for unset priority — empty string + displayEmpty overlaps InputLabel. */
const PRIORITY_UNSET = 'unset';
const ANALYTICS_TAB = 'analytics';
const OVERVIEW_TAB = 'overview';

const GAP_STATUS_LABELS = {
    identified: 'Identified',
    proposed: 'Proposed',
    promoted: 'Promoted to EEI',
};

const RELATIONSHIP_LABELS = {
    promoted: 'Promoted',
    proposed: 'Proposed',
};

function canEditGapPriority(role, roles) {
    return role === 'collection_manager'
        || role === 'admin'
        || (Array.isArray(roles) && roles.some((r) => r === 'collection_manager' || r === 'admin'));
}

function sessionAsAvatarProfile(user) {
    if (!user) return null;
    return {
        displayName: user.displayName || user.name || user.email || null,
        hasAvatar: user.hasAvatar === true,
        profilePicture: user.profilePicture || user.photoURL || undefined,
    };
}

function CoveragePctRow({label, pct}) {
    const track = pct == null ? '#eeeeee' : coverageTrackColor(pct);
    const fill = pct == null ? '#bdbdbd' : coverageScoreColor(pct);
    const fillWidth = pct == null ? 0 : Math.max(Math.min(pct, 100), pct > 0 ? 2 : 0);
    return (
        <Stack spacing={0.5} sx={{minWidth: 120, flex: 1}}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    {label}
                </Typography>
                <Typography
                    variant="caption"
                    fontWeight={700}
                    fontFamily='"Roboto Mono", monospace'
                    sx={{color: pct == null ? 'text.disabled' : fill}}
                >
                    {pct != null ? `${pct}%` : '—'}
                </Typography>
            </Stack>
            <Box sx={{width: '100%', height: 4, bgcolor: track, borderRadius: 1, overflow: 'hidden'}}>
                {fillWidth > 0 && (
                    <Box sx={{width: `${fillWidth}%`, height: '100%', bgcolor: fill}}/>
                )}
            </Box>
        </Stack>
    );
}

/**
 * Gap detail page: Overview (question, Targets, Related EEIs) | Collection |
 * Analytics | Similarity (CM/admin). Tags below tabs for CM/admin.
 * Create CT / collection-work APIs are intentionally omitted for Gaps.
 */
export default function GapPage() {
    const {gapNumber} = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const {user, role, roles} = useSession();
    const canManage = canEditGapPriority(role, roles);
    const canViewCollectionTab = canAccessCollectionTab(role, roles);
    const canViewSimilarity = canManage;
    const canEditTargets = canManage;

    const validTabs = useMemo(() => {
        const tabs = [OVERVIEW_TAB];
        if (canViewCollectionTab) tabs.push('collection');
        tabs.push(ANALYTICS_TAB);
        if (canViewSimilarity) tabs.push('similarity');
        return tabs;
    }, [canViewCollectionTab, canViewSimilarity]);
    const defaultTab = OVERVIEW_TAB;
    const urlTab = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(
        () => (validTabs.includes(urlTab) ? urlTab : defaultTab),
    );

    useEffect(() => {
        if (urlTab && validTabs.includes(urlTab) && urlTab !== activeTab) {
            setActiveTab(urlTab);
        } else if (urlTab && !validTabs.includes(urlTab) && activeTab !== defaultTab) {
            setActiveTab(defaultTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlTab, validTabs]);

    const changeTab = (value) => {
        setActiveTab(value);
        const next = new URLSearchParams(searchParams);
        next.set('tab', value);
        setSearchParams(next, {replace: true});
    };

    const [gap, setGap] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [prioritySaving, setPrioritySaving] = useState(false);
    const [relatedEeis, setRelatedEeis] = useState([]);
    const [relatedEeisLoading, setRelatedEeisLoading] = useState(false);

    const similarity = useGapSimilarity(gap?.id, canViewSimilarity && Boolean(gap?.id));

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getGapByNumber(gapNumber);
            if (!data) {
                setError('Gap not found');
                setGap(null);
            } else {
                setGap(data);
            }
        } catch (e) {
            setError(e?.message || 'Failed to load gap');
            setGap(null);
        } finally {
            setLoading(false);
        }
    }, [gapNumber]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!gap?.id || !canManage) {
            setRelatedEeis([]);
            setRelatedEeisLoading(false);
            return undefined;
        }
        let cancelled = false;
        setRelatedEeisLoading(true);
        getGapRelatedEeis(gap.id)
            .then((data) => {
                if (cancelled) return;
                setRelatedEeis(Array.isArray(data?.eeis) ? data.eeis : []);
            })
            .catch(() => {
                if (!cancelled) setRelatedEeis([]);
            })
            .finally(() => {
                if (!cancelled) setRelatedEeisLoading(false);
            });
        return () => { cancelled = true; };
    }, [gap?.id, canManage]);

    const authorUserId = gap?.authorUserId || null;
    const [authorProfile, setAuthorProfile] = useState(null);

    useEffect(() => {
        if (!authorUserId) {
            setAuthorProfile(null);
            return undefined;
        }
        if (authorUserId === user?.uid || authorUserId === user?.id) {
            setAuthorProfile(sessionAsAvatarProfile(user));
            return undefined;
        }
        let cancelled = false;
        Promise.resolve(getUserDoc(authorUserId))
            .then((doc) => {
                if (cancelled || !doc) return;
                setAuthorProfile(toAvatarProfile(doc));
            })
            .catch(() => {
                if (!cancelled) setAuthorProfile(null);
            });
        return () => { cancelled = true; };
    }, [authorUserId, user]);

    const authorDisplayName = userDisplayName(authorProfile)
        || gap?.authorDisplayName
        || gap?.author
        || authorUserId;

    const handleBack = () => {
        const returnTo = sanitizeReturnTo(location.state?.returnTo);
        if (returnTo) {
            navigate(returnTo);
            return;
        }
        navigate(-1);
    };

    const handlePriorityChange = async (event) => {
        const raw = event.target.value;
        const next = PRIORITY_VALUES.includes(raw) ? raw : null;
        const current = PRIORITY_VALUES.includes(gap?.priority) ? gap.priority : null;
        if ((next || null) === (current || null) || !gap?.id) return;
        setPrioritySaving(true);
        setError(null);
        try {
            const updated = await updateGapPriority(gap.id, next);
            setGap((prev) => (prev ? {...prev, priority: updated?.priority ?? next} : prev));
        } catch (e) {
            setError(e?.message || 'Failed to update priority');
        } finally {
            setPrioritySaving(false);
        }
    };

    const selectPriorityValue = PRIORITY_VALUES.includes(gap?.priority)
        ? gap.priority
        : PRIORITY_UNSET;

    const fintelPath = useMemo(() => (
        gap
            ? resolveGapFintelPath({
                type: 'GAP',
                reportId: gap.reportId,
                number: gap.number,
                id: gap.id,
            })
            : null
    ), [gap]);

    const statusLabel = GAP_STATUS_LABELS[gap?.status] || gap?.status || '—';
    const createdAtLabel = gap?.createdAt && dayjs(gap.createdAt).isValid()
        ? dayjs(gap.createdAt).format('MMM D, YYYY HH:mm')
        : null;
    const gapColor = TYPE_COLORS.GAP || '#757575';

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
                <Typography>Loading…</Typography>
            </Container>
        );
    }

    if (!gap) {
        return (
            <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
                <Stack spacing={2}>
                    <Typography color="error">{error || 'Not found'}</Typography>
                    <Button variant="outlined" size="small" onClick={handleBack}>
                        Back
                    </Button>
                </Stack>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{py: {xs: 2, md: 3}}}>
            <Paper
                elevation={0}
                sx={{
                    p: {xs: 2, sm: 3.5},
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Stack spacing={3}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Button variant="outlined" size="small" onClick={handleBack}>
                            Back
                        </Button>
                    </Stack>

                    <Stack
                        direction={{xs: 'column', sm: 'row'}}
                        justifyContent="space-between"
                        alignItems={{xs: 'flex-start', sm: 'center'}}
                        spacing={1.5}
                    >
                        <Stack spacing={0.5} sx={{minWidth: 0, flex: 1}}>
                            <Typography variant="h4" fontWeight={700} sx={{lineHeight: 1.2}}>
                                {gap.question || 'Gap'}
                            </Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{color: 'text.secondary'}}
                            >
                                <Chip
                                    size="small"
                                    label="GAP"
                                    sx={{
                                        height: 22,
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        bgcolor: `${gapColor}22`,
                                        color: gapColor,
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    color="inherit"
                                    component="span"
                                    fontFamily="monospace"
                                >
                                    {gap.number || gap.id}
                                </Typography>
                                {(authorUserId || authorDisplayName) && (
                                    <>
                                        <Typography variant="body2" color="inherit" component="span">•</Typography>
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                minWidth: 0,
                                                '& .MuiAvatar-root': {
                                                    width: 20,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                },
                                                '& .MuiLink-root': {
                                                    fontSize: '0.875rem',
                                                    color: 'text.secondary',
                                                    textDecoration: 'none',
                                                },
                                            }}
                                        >
                                            <AuthorChip
                                                userId={authorUserId}
                                                userDisplayName={authorDisplayName}
                                                flex={undefined}
                                                avatarProfile={authorProfile || gap.authorProfile || null}
                                            />
                                        </Box>
                                    </>
                                )}
                                {createdAtLabel && (
                                    <>
                                        <Typography variant="body2" color="inherit" component="span">•</Typography>
                                        <Typography variant="body2" color="inherit" component="span">
                                            {createdAtLabel}
                                        </Typography>
                                    </>
                                )}
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip label={statusLabel} size="small" variant="outlined"/>
                            {canManage ? (
                                <FormControl size="small" sx={{minWidth: 120}} disabled={prioritySaving}>
                                    <InputLabel id="gap-priority-label">Priority</InputLabel>
                                    <Select
                                        labelId="gap-priority-label"
                                        label="Priority"
                                        value={selectPriorityValue}
                                        onChange={handlePriorityChange}
                                    >
                                        <MenuItem value={PRIORITY_UNSET} aria-label="Priority not set">
                                            N/A
                                        </MenuItem>
                                        {PRIORITY_VALUES.map((p) => (
                                            <MenuItem key={p} value={p}>{p}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                <GapPriorityCell priority={gap.priority}/>
                            )}
                        </Stack>
                    </Stack>

                    {fintelPath && (
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" color="text.secondary">
                                Source FINTEL
                            </Typography>
                            <Link
                                component={RouterLink}
                                to={fintelPath}
                                variant="body2"
                                fontWeight={600}
                                underline="hover"
                            >
                                {gap.reportTitle || gap.reportId}
                            </Link>
                        </Stack>
                    )}

                    {error && (
                        <Typography color="error" variant="body2">{error}</Typography>
                    )}

                    <Tabs
                        value={activeTab}
                        onChange={(_, v) => changeTab(v)}
                        sx={{minHeight: 36, borderBottom: 1, borderColor: 'divider'}}
                    >
                        <Tab
                            value="overview"
                            label="Overview"
                            sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                        />
                        {canViewCollectionTab && (
                            <Tab
                                value="collection"
                                label="Collection"
                                sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                            />
                        )}
                        <Tab
                            value="analytics"
                            label="Analytics"
                            sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                        />
                        {canViewSimilarity && (
                            <Tab
                                value="similarity"
                                label="Similarity"
                                sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                            />
                        )}
                    </Tabs>

                    {activeTab === 'overview' && (
                        <Stack spacing={3}>
                            <Stack spacing={1.25}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Question
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{lineHeight: 1.75, maxWidth: '84ch'}}
                                >
                                    {gap.question || '—'}
                                </Typography>
                            </Stack>

                            <RequirementTargetsSection
                                value={gap.targets}
                                canEdit={canEditTargets && Boolean(gap.id)}
                                onSave={async (refs) => {
                                    const updated = await updateGapTargets(gap.id, refs);
                                    setGap((prev) => (prev ? {
                                        ...prev,
                                        targets: updated?.targets ?? refs,
                                        targetsLocked: updated?.targetsLocked ?? true,
                                    } : prev));
                                }}
                            />

                            {canManage && (
                                <Stack spacing={1} sx={{width: '100%', minWidth: 0}}>
                                    <Typography sx={TAGS_SECTION_HEADING_SX}>
                                        Related EEIs
                                    </Typography>
                                    {relatedEeisLoading ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Loading…
                                        </Typography>
                                    ) : relatedEeis.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            —
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.25}>
                                            {relatedEeis.map((eei) => {
                                                const num = eei.eeiNumber || eei.id;
                                                const rel = RELATIONSHIP_LABELS[eei.relationship]
                                                    || eei.relationship
                                                    || 'Related';
                                                return (
                                                    <Stack
                                                        key={eei.id || num}
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="flex-start"
                                                        flexWrap="wrap"
                                                        useFlexGap
                                                    >
                                                        <Chip
                                                            size="small"
                                                            label={rel}
                                                            variant="outlined"
                                                            sx={{height: 22, fontWeight: 600}}
                                                        />
                                                        <Link
                                                            component={RouterLink}
                                                            to={`/icr/eei/${encodeURIComponent(num)}`}
                                                            variant="body2"
                                                            fontWeight={600}
                                                            underline="hover"
                                                            sx={{minWidth: 0}}
                                                        >
                                                            {num}
                                                            {eei.text ? ` — ${eei.text}` : ''}
                                                        </Link>
                                                    </Stack>
                                                );
                                            })}
                                        </Stack>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )}

                    {activeTab === 'collection' && (
                        <RequirementCollectionPanel
                            tasks={[]}
                            coveragePct={gap.dataCoveragePct ?? null}
                            canCreateTask={false}
                            showTaskList={false}
                        />
                    )}

                    {activeTab === 'analytics' && (
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" fontWeight={700}>Coverage</Typography>
                            <CoveragePctRow label="Analytic" pct={gap.analyticCoveragePct}/>
                        </Stack>
                    )}

                    {canViewSimilarity && activeTab === 'similarity' && (
                        <EeiSimilaritySection similarity={similarity}/>
                    )}

                    {canManage && gap.id && (
                        <>
                            <Divider/>
                            <EntityTagsSection
                                entityType="GAP"
                                entityId={gap.id}
                                canEdit={canManage}
                                enabled={canManage}
                            />
                        </>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
